import { io, Socket } from "socket.io-client";
import { config } from "../config";
import { assetsApiRequest } from "../api/api-request";
import { getAccessToken } from "../utils/session";
import { Point, PuckValue } from "../point/point.model";

// Prevent retrying authentication more than 5 times if user is not able to authenticate for some reason
const MAX_AUTHENTICATION_RETRIES = 5;

export class PuckValueSocketClient {
  private socket: Socket | null;
  private companyId: number;
  private points: Point[];
  private onValuesCallback: (values: PuckValue[]) => void;

  private authenticationRetryCount = 0;

  constructor(
    companyId: number,
    pointsToSubscribe: Point[],
    onValuesCallback: (values: PuckValue[]) => void,
  ) {
    this.companyId = companyId;
    this.points = pointsToSubscribe;
    this.onValuesCallback = onValuesCallback;

    this.socket = io(config.API_URL, {
      transports: ["websocket"],
      reconnection: true,
    });
    this.socket.on("connect", this.onConnect);
    this.socket.on("error", this.onError);
    this.socket.on("values", this.onValuesCallback);
  }

  private onConnect = () => {
    if (!this.socket) {
      return;
    }

    const token = getAccessToken();

    this.socket.emit("open", {
      token: "Bearer " + token,
    });

    this.socket.timeout(5000).emit(
      "subscribe",
      {
        companyId: this.companyId,
        points: this.points,
        token: "Bearer " + token,
      },
      (error: Error | null, response?: { success: boolean }) => {
        if (!error && response?.success) {
          this.authenticationRetryCount = 0;
        }
      },
    );
  };

  public close = () => {
    if (!this.socket) {
      return;
    }

    this.socket.off("connect", this.onConnect);
    this.socket.off("error", this.onError);
    this.socket.off("values", this.onValuesCallback);
    this.socket.disconnect();
    // Set to null to prevent reuse
    this.socket = null;
  };

  public disconnect = () => {
    if (!this.socket || this.socket.disconnected) {
      return;
    }
    this.socket.disconnect();
  };

  public connect = () => {
    if (!this.socket || this.socket.connected) {
      return;
    }
    this.socket.connect();
  };

  private onError = (ev: { code: number; message: string }) => {
    if (!this.socket) return;
    if (
      ev.code === 401 &&
      ++this.authenticationRetryCount <= MAX_AUTHENTICATION_RETRIES
    ) {
      assetsApiRequest.refreshToken(this.companyId).then(() => {
        if (!this.socket) return;
        this.socket.disconnect();
        this.socket.close();
        this.socket.connect();
      });
    } else {
      alert(ev.message);
    }
  };
}
